import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Bell,
  Bot,
  Building2,
  Database,
  FileText,
  Flag,
  Gauge,
  Home,
  Layers,
  MonitorSmartphone,
  Scale,
  Server,
  Shield,
  Sparkles,
  CalendarDays,
  Users,
} from "lucide-react";

import type { PermissionActionId } from "../../types/collaboratorPermissionRegistry";

export type DelivaiControlNavItem = {
  to: string;
  end?: boolean;
  label: string;
  /** Chave i18n (`dev.nav.*`). Se ausente, usa `label`. */
  labelKey?: string;
  title: string;
  titleKey?: string;
  icon: LucideIcon;
  /** Ação exigida no módulo delivai_controle (além de ver o painel). */
  devAction?: PermissionActionId;
  /** Card na tela de início do painel interno. */
  homeCard?: {
    title: string;
    desc: string;
    accent: string;
    titleKey?: string;
    descKey?: string;
  };
};

/** Rotas do painel interno (sidebar + layout). */
export const DELIVAI_CONTROL_NAV_ITEMS: DelivaiControlNavItem[] = [
  {
    to: "/dev",
    end: true,
    label: "Início",
    labelKey: "dev.nav.home",
    title: "Início",
    titleKey: "dev.nav.home",
    icon: Home,
  },
  {
    to: "/dev/licenses",
    label: "Licenças",
    labelKey: "dev.nav.licenses",
    title: "Licenças",
    titleKey: "dev.nav.licenses",
    icon: Layers,
    devAction: "editar",
    homeCard: {
      title: "Licenças e planos",
      desc: "Consultar empresa por CNPJ, ajustar dias restantes e pay_types exibidos na tela de licença.",
      accent: "from-violet-500/25 to-fuchsia-500/10",
      titleKey: "dev.card.licenses.title",
      descKey: "dev.card.licenses.desc",
    },
  },
  {
    to: "/dev/reports",
    label: "Reports",
    labelKey: "dev.nav.reports",
    title: "Reports",
    titleKey: "dev.nav.reports_title",
    icon: Flag,
    homeCard: {
      title: "Reports",
      desc: "Avisos enviados pela bandeira nas telas da loja, com o texto e as imagens.",
      accent: "from-orange-500/25 to-amber-500/10",
      titleKey: "dev.card.reports.title",
      descKey: "dev.card.reports.desc",
    },
  },
  {
    to: "/dev/contracts",
    label: "Contratos",
    labelKey: "dev.nav.contracts",
    title: "Contratos e minutas jurídicas",
    titleKey: "dev.nav.contracts_title",
    icon: Scale,
    devAction: "editar_contratos",
    homeCard: {
      title: "Contratos jurídicos",
      desc: "Revisar cláusulas B2B por módulo, termos do consumidor, termo da equipe jurídica e preview com hash/versão.",
      accent: "from-amber-500/20 to-yellow-600/10",
      titleKey: "dev.card.contracts.title",
      descKey: "dev.card.contracts.desc",
    },
  },
  {
    to: "/dev/evolution",
    label: "Evolution",
    labelKey: "dev.nav.evolution",
    title: "Evolution",
    titleKey: "dev.nav.evolution",
    icon: Server,
    devAction: "criar",
    homeCard: {
      title: "Evolution API",
      desc: "Listar instâncias, criar nova instância e ferramentas de conexão/QR.",
      accent: "from-sky-500/25 to-cyan-500/10",
    },
  },
  {
    to: "/dev/bot",
    label: "Bot",
    labelKey: "dev.nav.bot",
    title: "Controle do bot WhatsApp",
    titleKey: "dev.nav.bot_title",
    icon: Bot,
    devAction: "editar",
    homeCard: {
      title: "Bot WhatsApp",
      desc: "Kill-switch global e liga/desliga por loja. Com global OFF o botão da loja fica indisponível.",
      accent: "from-rose-500/25 to-red-600/10",
      titleKey: "dev.card.bot.title",
      descKey: "dev.card.bot.desc",
    },
  },
  {
    to: "/dev/delidesk",
    label: "DeliDesk",
    labelKey: "dev.nav.delidesk",
    title: "Funções globais do DeliDesk",
    titleKey: "dev.nav.delidesk_title",
    icon: MonitorSmartphone,
    devAction: "editar",
    homeCard: {
      title: "DeliDesk",
      desc: "Liga/desliga funções globais (Printer, etc.). Com Printer OFF, lista agents mas não imprime nem captura.",
      accent: "from-emerald-500/25 to-teal-600/10",
      titleKey: "dev.card.delidesk.title",
      descKey: "dev.card.delidesk.desc",
    },
  },
  {
    to: "/dev/cardapio",
    label: "Empresas",
    labelKey: "dev.nav.companies",
    title: "Companies (registro companies)",
    titleKey: "dev.nav.companies",
    icon: Building2,
    devAction: "editar_cardapio",
    homeCard: {
      title: "Companies",
      desc: "Carregar e editar o registro completo da empresa (tabela companies) ou criar via webhook.",
      accent: "from-delivai-neon-green/30 to-emerald-600/10",
    },
  },
  {
    to: "/dev/api-keys",
    label: "Chaves de API",
    labelKey: "dev.nav.platform_ai",
    title: "Chaves de API DelivAI",
    titleKey: "dev.nav.platform_ai_title",
    icon: Sparkles,
    devAction: "editar",
    homeCard: {
      title: "Chaves de API",
      desc: "Chaves por serviço (Atendimento, Fast Order) usadas por todos os clientes sem chave própria.",
      accent: "from-delivai-neon-green/25 to-cyan-600/10",
      titleKey: "dev.card.platform_ai.title",
      descKey: "dev.card.platform_ai.desc",
    },
  },
  {
    to: "/dev/agenda",
    label: "Agenda",
    labelKey: "dev.nav.agenda",
    title: "Agenda — defaults e templates",
    titleKey: "dev.nav.agenda_title",
    icon: CalendarDays,
    devAction: "editar",
    homeCard: {
      title: "Agenda",
      desc: "Defaults/tetos da agenda e templates WhatsApp (plataforma e por loja).",
      accent: "from-sky-500/20 to-indigo-600/10",
      titleKey: "dev.card.agenda.title",
      descKey: "dev.card.agenda.desc",
    },
  },
  {
    to: "/dev/prompts",
    label: "Prompts",
    labelKey: "dev.nav.prompts",
    title: "Prompts do bot",
    titleKey: "dev.nav.prompts",
    icon: FileText,
    devAction: "editar_prompts",
    homeCard: {
      title: "Prompts do bot",
      desc: "CRUD da tabela prompt_templates — instruções e corpo por nicho via webhook n8n.",
      accent: "from-amber-500/20 to-orange-500/10",
    },
  },
  {
    to: "/dev/clientes",
    label: "Clientes",
    labelKey: "dev.nav.consumers",
    title: "Clientes da empresa (consumers)",
    titleKey: "dev.nav.consumers",
    icon: Users,
    devAction: "editar_consumers",
    homeCard: {
      title: "Clientes da empresa",
      desc: "Listar consumers vinculados à loja e mesclar duplicatas por telefone.",
      accent: "from-cyan-500/25 to-sky-600/10",
    },
  },
  {
    to: "/dev/database",
    label: "Banco",
    labelKey: "dev.nav.database",
    title: "Banco de dados (Studio interno)",
    titleKey: "dev.nav.database",
    icon: Database,
    devAction: "ver_banco",
    homeCard: {
      title: "Banco de dados",
      desc: "Studio interno para inspecionar tabelas, filtrar registros e operações de suporte.",
      accent: "from-indigo-500/25 to-violet-600/10",
      titleKey: "dev.card.database.title",
      descKey: "dev.card.database.desc",
    },
  },
  {
    to: "/dev/logs",
    label: "Logs",
    labelKey: "dev.nav.logs",
    title: "Executions / logs em tempo real",
    titleKey: "dev.nav.logs",
    icon: Activity,
    devAction: "ver_logs",
    homeCard: {
      title: "Executions / logs",
      desc: "Acompanhar execuções do n8n e fluxos em tempo real para debug de integrações.",
      accent: "from-emerald-500/20 to-teal-600/10",
      titleKey: "dev.card.logs.title",
      descKey: "dev.card.logs.desc",
    },
  },
  {
    to: "/dev/observability",
    label: "Observabilidade",
    labelKey: "dev.nav.observability",
    title: "Métricas (Prometheus) + Logs (Loki)",
    titleKey: "dev.nav.observability",
    icon: Gauge,
    devAction: "ver_observabilidade",
    homeCard: {
      title: "Observabilidade",
      desc: "Métricas (Prometheus) e logs agregados (Loki) do ambiente DelivAI.",
      accent: "from-orange-500/20 to-amber-600/10",
      titleKey: "dev.card.observability.title",
      descKey: "dev.card.observability.desc",
    },
  },
  {
    to: "/dev/ops-alerts",
    label: "Alertas",
    labelKey: "dev.nav.ops_alerts",
    title: "Alertas operacionais",
    titleKey: "dev.nav.ops_alerts",
    icon: Bell,
    devAction: "editar",
    homeCard: {
      title: "Alertas operacionais",
      desc: "Rotas de alerta (Google Calendar, WhatsApp…) e quem recebe em queda em massa.",
      accent: "from-amber-500/25 to-orange-600/10",
      titleKey: "dev.card.ops_alerts.title",
      descKey: "dev.card.ops_alerts.desc",
    },
  },
  {
    to: "/dev/permissoes",
    label: "Permissões",
    labelKey: "dev.nav.permissions",
    title: "Permissões dev",
    titleKey: "dev.nav.permissions",
    icon: Shield,
    devAction: "editar_permissoes",
    homeCard: {
      title: "Permissões de devs",
      desc: "Matriz de cargo no Postgres (`colaboradores.cargo`) — visão geral e próximos passos.",
      accent: "from-rose-500/20 to-red-500/10",
    },
  },
];

export const DELIVAI_CONTROL_HOME_CARDS = DELIVAI_CONTROL_NAV_ITEMS.filter(
  (item): item is DelivaiControlNavItem & { homeCard: NonNullable<DelivaiControlNavItem["homeCard"]> } =>
    Boolean(item.homeCard)
);

export function isDelivaiControlNavActive(pathname: string, item: DelivaiControlNavItem): boolean {
  if (item.end) {
    return pathname === item.to || pathname === `${item.to}/`;
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function isDelivaiControlHomePath(pathname: string): boolean {
  return pathname === "/dev" || pathname === "/dev/";
}

export function devNavText(
  item: Pick<DelivaiControlNavItem, "label" | "labelKey" | "title" | "titleKey">,
  t: (key: string) => string,
  field: "label" | "title"
): string {
  const key = field === "label" ? item.labelKey : item.titleKey;
  const fallback = field === "label" ? item.label : item.title;
  return key ? t(key) : fallback;
}

/** Filtra itens do menu interno conforme cargo (ex.: advogado só vê Contratos). */
export function filterDelivaiControlNavItems<T extends DelivaiControlNavItem>(
  items: T[],
  canAction: (actionId: PermissionActionId) => boolean
): T[] {
  return items.filter((item) => {
    if (item.end) return canAction("ver");
    if (!item.devAction) return canAction("ver");
    return canAction(item.devAction);
  });
}
